import { type FC, useState } from 'react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Filter } from 'lucide-react'

export interface DashboardTransactionFilters {
    search?: string
    status?: 'COMPLETED' | 'PENDING' | 'FAILED' | string
    cryptocurrency?: string
}

interface DashboardFilterDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    currentFilters: DashboardTransactionFilters
    onApplyFilters: (filters: DashboardTransactionFilters) => void
}

export const DashboardFilterDialog: FC<DashboardFilterDialogProps> = ({
    open,
    onOpenChange,
    currentFilters,
    onApplyFilters,
}) => {
    const [filters, setFilters] =
        useState<DashboardTransactionFilters>(currentFilters)

    const handleApply = () => {
        onApplyFilters(filters)
        onOpenChange(false)
    }

    const handleReset = () => {
        const resetFilters: DashboardTransactionFilters = {}
        setFilters(resetFilters)
        onApplyFilters(resetFilters)
        onOpenChange(false)
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <div className="rounded-full bg-blue-100 p-2">
                            <Filter className="h-4 w-4 text-blue-600" />
                        </div>
                        <DialogTitle>Filter Recent Transactions</DialogTitle>
                    </div>
                    <DialogDescription>
                        Apply filters to narrow down the recent transaction list
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Search */}
                    <div className="space-y-2">
                        <Label htmlFor="search">Search</Label>
                        <Input
                            id="search"
                            placeholder="Search by transaction ID, account name, or wallet..."
                            value={filters.search || ''}
                            onChange={e =>
                                setFilters({
                                    ...filters,
                                    search: e.target.value || undefined,
                                })
                            }
                        />
                    </div>

                    {/* Status */}
                    <div className="space-y-2">
                        <Label htmlFor="status">Status</Label>
                        <Select
                            value={(filters.status as string) || 'all'}
                            onValueChange={(value: string) =>
                                setFilters({
                                    ...filters,
                                    status: value === 'all' ? undefined : value,
                                })
                            }
                        >
                            <SelectTrigger id="status">
                                <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Status</SelectItem>
                                <SelectItem value="COMPLETED">
                                    Completed
                                </SelectItem>
                                <SelectItem value="PENDING">Pending</SelectItem>
                                <SelectItem value="FAILED">Failed</SelectItem>
                                <SelectItem value="CANCELLED">
                                    Cancelled
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Cryptocurrency */}
                    <div className="space-y-2">
                        <Label htmlFor="cryptocurrency">Cryptocurrency</Label>
                        <Select
                            value={filters.cryptocurrency || 'all'}
                            onValueChange={(value: string) =>
                                setFilters({
                                    ...filters,
                                    cryptocurrency:
                                        value === 'all' ? undefined : value,
                                })
                            }
                        >
                            <SelectTrigger id="cryptocurrency">
                                <SelectValue placeholder="Select cryptocurrency" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">
                                    All Cryptocurrencies
                                </SelectItem>
                                <SelectItem value="BTC">
                                    Bitcoin (BTC)
                                </SelectItem>
                                <SelectItem value="ETH">
                                    Ethereum (ETH)
                                </SelectItem>
                                <SelectItem value="USDT">
                                    Tether (USDT)
                                </SelectItem>
                                <SelectItem value="USDC">
                                    USD Coin (USDC)
                                </SelectItem>
                                <SelectItem value="DOGE">
                                    Dogecoin (DOGE)
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={handleReset}>
                        Reset Filters
                    </Button>
                    <Button onClick={handleApply}>Apply Filters</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
